import React, { useState } from "react";
import { HeartHandshake, Clock, ChevronDown, Hourglass } from "lucide-react";
import { Card } from "@/components/ui/card";
import { fmtDate, fmtTime } from "@/components/PageHeader";

function ShiftRow({ s, past }) {
  return (
    <div className={`flex items-start gap-3 py-3 ${past ? "opacity-70" : ""}`} data-testid={`child-shift-${s.id}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><HeartHandshake className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-800">{s.sheet_title}</p>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-600">{fmtDate(s.event_date)}</span>
          <span className="flex items-center gap-1 tabular-nums"><Clock className="h-3 w-3" /> {fmtTime(s.start_time)} – {fmtTime(s.end_time)}</span>
          {s.label && <span>· {s.label}</span>}
        </p>
        {s.description && <p className="mt-0.5 text-xs text-slate-400">{s.description}</p>}
      </div>
      {s.given_up && !past && (
        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`child-shift-given-up-${s.id}`}>Given up — waiting for coach</span>
      )}
    </div>
  );
}

// Collapsible "Past shifts (N)" card with hours served — shared by the parent and member Volunteer pages.
export function PastShiftsCard({ past = [], hours = 0 }) {
  const [showPast, setShowPast] = useState(false);
  if (past.length === 0) return null;
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="past-shifts-card">
      <button type="button" onClick={() => setShowPast((v) => !v)} className="flex w-full items-center justify-between gap-3 text-left" data-testid="child-shifts-past-toggle">
        <span className="font-heading text-lg font-bold text-slate-800">Past shifts <span className="text-sm font-medium text-slate-400">({past.length})</span></span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 tabular-nums" data-testid="child-shifts-hours">
            <Hourglass className="h-3.5 w-3.5" /> {hours} hr{hours === 1 ? "" : "s"} served
          </span>
          <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${showPast ? "rotate-180" : ""}`} />
        </span>
      </button>
      {showPast && <div className="mt-2 divide-y divide-slate-100" data-testid="child-shifts-past">{past.map((s) => <ShiftRow key={s.id} s={s} past />)}</div>}
    </Card>
  );
}

// Parent view (read-only): the child's upcoming volunteer shifts + collapsible past shifts with hours served.
export default function ChildVolunteerShifts({ data }) {
  const { upcoming = [], past = [], past_hours: hours = 0 } = data;
  return (
    <div className="space-y-4" data-testid="child-volunteer-shifts">
      <Card className="border-slate-200 p-5 shadow-sm">
        <h3 className="mb-1 font-heading text-lg font-bold text-slate-800">Upcoming volunteer shifts</h3>
        {upcoming.length === 0
          ? <p className="py-6 text-center text-sm text-slate-400" data-testid="child-shifts-empty">No upcoming volunteer shifts.</p>
          : <div className="divide-y divide-slate-100" data-testid="child-shifts-upcoming">{upcoming.map((s) => <ShiftRow key={s.id} s={s} />)}</div>}
      </Card>
      <PastShiftsCard past={past} hours={hours} />
    </div>
  );
}
